import { memo, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faFilter } from "@fortawesome/free-solid-svg-icons";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  Tooltip as RTooltip,
} from "recharts";
import ChartCard from "../ChartCard";
import { chartPalette } from "./chartPalette";
import { getTooltipProps } from "./tooltipProps";

function TopDonorsChart({
  topDonors = [],
  requests = [],
  listingById,
  materialOptions = [],
  darkMode,
}) {
  const { t } = useTranslation();
  const [selectedMat, setSelectedMat] = useState("all");

  const axisColor = darkMode ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)";
  const gridColor = darkMode ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)";

  // when filtering by material, recompute from raw requests
  const displayData = useMemo(() => {
    if (selectedMat === "all") {
      return topDonors.slice(0, 10).map((d) => ({
        name: d.name || d.key,
        value: d.value,
      }));
    }

    // group requests by donor for the selected material
    const agg = new Map();
    for (const r of requests) {
      if (!r?.date_delivered) continue;
      const listing = listingById?.get(String(r.listing_id));
      const mat =
        listing?.material ||
        listing?.materialType ||
        listing?.material_name ||
        listing?.material_type;
      if (mat !== selectedMat) continue;

      const qty =
        Number(r.quantity_delivered) || Number(r.quantity) || Number(r.qty) || 0;
      const companyId =
        r.company_id || r.companyId || r.user_id || r.userId || r.donor_id || r.donorId || listing?.company_id;
      const key = String(companyId || "unknown");
      const label =
        listing?.donor_name || listing?.company_name || listing?.company || key;

      const prev = agg.get(key) || { name: label, value: 0 };
      prev.value += qty;
      agg.set(key, prev);
    }

    return [...agg.values()]
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [topDonors, requests, listingById, selectedMat]);

  const chartHeight = Math.max(300, displayData.length * 45);

  const filter = materialOptions.length > 0 && (
    <div className="rmo__chart-filter">
      <FontAwesomeIcon icon={faFilter} className="rmo__filter-icon" />
      <select
        value={selectedMat}
        onChange={(e) => setSelectedMat(e.target.value)}
        className="rmo__filter-select"
      >
        <option value="all">{t("All Materials")}</option>
        {materialOptions.map((m) => (
          <option key={m} value={m}>{m}</option>
        ))}
      </select>
    </div>
  );

  const totalLbs = displayData.reduce((s, d) => s + d.value, 0);

  return (
    <ChartCard
      icon={faBuilding}
      iconColor="blue"
      title={t("Top Donors")}
      badge={displayData.length > 0 ? `${displayData.length} ${t("donors")}` : null}
      actions={filter}
      summary={
        displayData.length > 0
          ? [{ label: t("Total Received"), value: `${totalLbs.toLocaleString()} lbs` }]
          : null
      }
    >
      {displayData.length === 0 ? (
        <div className="chart-empty-state">
          <FontAwesomeIcon icon={faBuilding} className="empty-icon" />
          <p>{t("No donor data for selected filters")}</p>
        </div>
      ) : (
        <div style={{ height: chartHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={displayData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={gridColor}
                horizontal={false}
              />
              <XAxis
                type="number"
                tick={{ fontSize: 12, fill: axisColor }}
                axisLine={{ stroke: axisColor }}
                tickLine={{ stroke: axisColor }}
                tickFormatter={(v) => v.toLocaleString()}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={150}
                tick={{ fontSize: 12, fill: axisColor }}
                axisLine={{ stroke: axisColor }}
                tickLine={{ stroke: axisColor }}
                tickFormatter={(v) =>
                  v.length > 20 ? `${v.substring(0, 18)}…` : v
                }
              />
              <RTooltip
                {...getTooltipProps(darkMode)}
                formatter={(v) => [`${Number(v).toLocaleString()} lbs`, t("Total")]}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={35}>
                {displayData.map((_, i) => (
                  <Cell key={i} fill={chartPalette[i % chartPalette.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </ChartCard>
  );
}

// memoized — skips re-render when parent state (e.g. modal open) changes but
// our props stay referentially equal. recharts animations are expensive so this
// is a meaningful win on pages with frequent unrelated state changes.
export default memo(TopDonorsChart);
